import {
  Box,
  Button,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import Crossicon from "../../Icon/Crossicon";
import { apiErrorMessage } from "../../helper/apiError";
import { fontSize } from "../theme/fontSize";
import { fontWeight } from "../theme/fontWeight";
import { FontFamily } from "../theme/fontFamily";
import { ThemeColor } from "../theme/color";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import {
  getUnivByOrgIdService,
  getTemplateDegreesService,
} from "../../Redux/service/Application";
import CustomButton from "../button/button";
import { Form, Formik } from "formik";
import axios from "axios";
import { basicurl, token } from "../../Redux/ServiceUrl/url";
import CustomSelect from "../select field/OutlinedSelectField";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { sm: "100%", xs: "100%", md: "50%", lg: "42%" },
  maxHeight: "85vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

export const DownloadBulkUploadTemplateModal = ({ open, handleClose }) => {
  const dispatch = useDispatch();

  const [Universitylist, setUniversitylist] = useState([]);
  const [Degreelist, setDegreelist] = useState([]);
  const [Organizationlist, setOrganizationlist] = useState([]);
  const [RoleTypeAgent, setRoleTypeAgent] = useState(null);
  const [loadingUniv, setLoadingUniv] = useState(false);
  const [loadingDegrees, setLoadingDegrees] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const OrganizationlistState = useSelector(
    (state) => state.setting?.organization?.data?.organizations
  );

  const AgentlistState = useSelector(
    (state) => state.setting?.agent?.data?.activeAgents
  );

  // Detect Agent user and agent's organization
  useEffect(() => {
    if (window.sessionStorage.getItem("user_role_type") === "agent") {
      const currentAgent = AgentlistState?.find(
        (data) => data?._id === window.sessionStorage.getItem("user_id")
      );
      setRoleTypeAgent(currentAgent);
    } else {
      setRoleTypeAgent(null);
    }
  }, [AgentlistState]);

  // Load organizations for CAE admin/user
  useEffect(() => {
    setOrganizationlist(
      OrganizationlistState?.map(({ organizationName, ...rest }) => ({
        title: organizationName,
        ...rest,
      })) || []
    );
  }, [OrganizationlistState]);

  // Load universities when organization changes
  const handleUnivByOrgId = async (orgId) => {
    if (!orgId) {
      setUniversitylist([]);
      return;
    }
    setLoadingUniv(true);
    try {
      const res = await getUnivByOrgIdService(orgId);
      if (res && res.status === 200) {
        setUniversitylist(
          res.data.universities?.map(({ name, ...rest }) => ({
            title: name,
            ...rest,
          })) || []
        );
      } else {
        setUniversitylist([]);
      }
    } catch (err) {
      console.error("Error fetching universities by organization ID:", err);
      setUniversitylist([]);
    } finally {
      setLoadingUniv(false);
    }
  };

  // When modal opens: if agent, load universities for their organization
  useEffect(() => {
    if (open) {
      const agentOrgId = RoleTypeAgent?.organization?._id;
      if (agentOrgId) {
        handleUnivByOrgId(agentOrgId);
      }
    } else {
      // Clear on close
      setDegreelist([]);
      setUniversitylist([]);
    }
  }, [open, RoleTypeAgent]);

  // Handle University change -> fetch only degrees with commission and program configured
  const handleUniversityChange = async (univId, orgId, setFieldValue) => {
    setFieldValue("universityId", univId);
    setFieldValue("degreeId", "");
    setDegreelist([]);

    if (!univId || !orgId) return;

    setLoadingDegrees(true);
    try {
      const res = await getTemplateDegreesService(orgId, univId);
      if (res && res.status === 200 && Array.isArray(res.data?.degrees)) {
        setDegreelist(
          res.data.degrees.map((deg) => ({
            _id: deg._id,
            title: deg.title || deg.name,
          }))
        );
      } else {
        setDegreelist([]);
      }
    } catch (err) {
      console.error("Error fetching degrees for template:", err);
      setDegreelist([]);
    } finally {
      setLoadingDegrees(false);
    }
  };

  const validationSchema = Yup.object().shape({
    organizationId: Yup.string().required("Organization is required"),
    universityId: Yup.string().required("University is required"),
    degreeId: Yup.string().required("Degree is required"),
  });

  const handleGetBulkTemplate = async (values) => {
    setDownloading(true);
    const currentToken = window.sessionStorage.getItem("user_token") || token;
    try {
      const response = await axios.get(
        `${basicurl}/api/student/download-student-template?organizationId=${values.organizationId}&universityId=${values.universityId}&degreeId=${values.degreeId}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "student_bulk.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      dispatch(showSnackbar("Template downloaded successfully", "success"));
      handleClose();
    } catch (error) {
      if (
        error.response &&
        error.response.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errMsg = JSON.parse(reader.result);
            dispatch(
              showSnackbar(errMsg?.message || "Something went wrong", "error")
            );
          } catch (e) {
            dispatch(showSnackbar("Something went wrong", "error"));
          }
        };
        reader.readAsText(error.response.data);
      } else {
        dispatch(
          showSnackbar(
            apiErrorMessage(error, "Error downloading template"),
            "error"
          )
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  const initialOrgId = RoleTypeAgent?.organization?._id || "";

  return (
    <Modal
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography
          align="right"
          sx={{ cursor: "pointer" }}
          onClick={() => handleClose()}
        >
          <Crossicon width={"20"} height={"20"} />
        </Typography>

        <Typography
          fontFamily={FontFamily.Poppins}
          fontWeight={fontWeight.W600}
          fontSize={fontSize.h3.large}
          color={ThemeColor.BLACK}
          align="center"
          sx={{ mb: 3 }}
        >
          Download Template
        </Typography>

        <Formik
          enableReinitialize={true}
          initialValues={{
            organizationId: initialOrgId,
            degreeId: "",
            universityId: "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            handleGetBulkTemplate(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
          }) => (
            <Form className="w-100 d-flex flex-column justify-content-center align-item-center">
              <div>
                {/* Organization Select */}
                <div className="mb-2">
                  <CustomSelect
                    value={values.organizationId || ""}
                    name={"organizationId"}
                    multiple={false}
                    searchable={true}
                    option={
                      RoleTypeAgent
                        ? [
                            {
                              _id: RoleTypeAgent?.organization?._id,
                              title:
                                RoleTypeAgent?.organization?.organizationName ||
                                "My Organization",
                            },
                          ]
                        : Organizationlist
                    }
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const newOrgId = e.target.value;
                      setFieldValue("organizationId", newOrgId);
                      setFieldValue("universityId", "");
                      setFieldValue("degreeId", "");
                      setDegreelist([]);
                      handleUnivByOrgId(newOrgId);
                    }}
                    fullWidth={true}
                    disabled={Boolean(RoleTypeAgent)}
                    label={"Organization Name *"}
                    helperText={
                      <Typography
                        align="left"
                        color={
                          errors.organizationId && touched.organizationId
                            ? ThemeColor.RED
                            : ThemeColor.GREY
                        }
                        fontSize={fontSize.h4.medium}
                        fontFamily={FontFamily.Lato}
                        fontWeight={fontWeight.W500}
                      >
                        {errors.organizationId && touched.organizationId
                          ? errors.organizationId
                          : ""}
                      </Typography>
                    }
                  />
                </div>

                {/* University Select */}
                <div className="mb-2">
                  <CustomSelect
                    value={values.universityId || ""}
                    name={"universityId"}
                    multiple={false}
                    searchable={true}
                    disabled={!values.organizationId || loadingUniv}
                    option={Universitylist}
                    onChange={(e) => {
                      handleUniversityChange(
                        e.target.value,
                        values.organizationId,
                        setFieldValue
                      );
                    }}
                    fullWidth={true}
                    label={"University *"}
                    helperText={
                      <Typography
                        align="left"
                        color={
                          errors.universityId && touched.universityId
                            ? ThemeColor.RED
                            : ThemeColor.GREY
                        }
                        fontSize={fontSize.h4.medium}
                        fontFamily={FontFamily.Lato}
                        fontWeight={fontWeight.W500}
                      >
                        {errors.universityId && touched.universityId
                          ? errors.universityId
                          : loadingUniv
                          ? "Loading universities..."
                          : !values.organizationId
                          ? "Select an organization first"
                          : Universitylist.length === 0
                          ? "No commissioned universities found for this organization"
                          : ""}
                      </Typography>
                    }
                  />
                </div>

                {/* Degree Select */}
                <div className="mb-2">
                  <CustomSelect
                    value={values.degreeId || ""}
                    name={"degreeId"}
                    multiple={false}
                    searchable={true}
                    disabled={!values.universityId || loadingDegrees}
                    option={Degreelist}
                    onChange={(e) => setFieldValue("degreeId", e.target.value)}
                    fullWidth={true}
                    label={"Degree *"}
                    helperText={
                      <Typography
                        align="left"
                        color={
                          errors.degreeId && touched.degreeId
                            ? ThemeColor.RED
                            : ThemeColor.GREY
                        }
                        fontSize={fontSize.h4.medium}
                        fontFamily={FontFamily.Lato}
                        fontWeight={fontWeight.W500}
                      >
                        {errors.degreeId && touched.degreeId
                          ? errors.degreeId
                          : loadingDegrees
                          ? "Loading commissioned degrees..."
                          : !values.universityId
                          ? "Select university first"
                          : Degreelist.length === 0
                          ? "No commissioned degrees found for this university"
                          : ""}
                      </Typography>
                    }
                  />
                </div>
              </div>

              <div className="px-3 mt-4 w-100 align-items-center d-flex justify-content-center">
                <CustomButton
                  variant="contained"
                  type="submit"
                  backgroundColor={ThemeColor.PURPLE}
                  color={ThemeColor.WHITE}
                  disabled={downloading || !values.organizationId || !values.universityId || !values.degreeId}
                >
                  {downloading ? "Downloading..." : "Download Template"}
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
